import React from "react";
import * as Sentry from "@sentry/react";
import { isRealtimeError } from "../../hooks/wss/realtimeError";

interface RealtimeErrorBoundaryProps {
  /** The tree with the realtime provider. */
  children: React.ReactNode;
  /**
   * The same tree WITHOUT the provider. Its consumers read the websocket
   * context's inert defaults (not authenticated, no-op subscribe), so the app
   * keeps working on REST polling instead of unmounting.
   */
  fallback: React.ReactNode;
}

interface RealtimeErrorBoundaryState {
  failed: boolean;
  /** A non-realtime error, handed on to the next boundary up. */
  passThrough?: unknown;
}

/**
 * Contains a failure OF THE REALTIME LAYER (FE-11): an error marked as a
 * `RealtimeError` switches the app to the provider-less fallback, which keeps it
 * on screen on REST polling.
 *
 * This boundary wraps nearly the whole app, so it must not swallow anything
 * else (H3): an unrelated render error is rethrown to the app-level boundary
 * instead of silently remounting every page without live streams. Frame
 * handlers contain their own exceptions (`forEachFrame`), so a bad frame never
 * gets here at all.
 *
 * Switching to the fallback remounts the tree once; there is no automatic
 * retry, which would remount it again. An error thrown by the fallback itself
 * propagates to the next boundary.
 */
export class RealtimeErrorBoundary extends React.Component<
  RealtimeErrorBoundaryProps,
  RealtimeErrorBoundaryState
> {
  state: RealtimeErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(error: unknown): Partial<RealtimeErrorBoundaryState> {
    return isRealtimeError(error) ? { failed: true } : { passThrough: error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (!isRealtimeError(error)) return;
    Sentry.captureException?.(error, {
      tags: { area: "realtime" },
      extra: {
        label: "Realtime provider failed; continuing without live streams",
        componentStack: info.componentStack,
      },
    });
    // eslint-disable-next-line no-console
    console.error("[realtime] provider failed; continuing without live streams", error);
  }

  render() {
    if (this.state.passThrough !== undefined) throw this.state.passThrough;
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export default RealtimeErrorBoundary;
